'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import {
  useAccount,
  useConnect,
  useSignTypedData,
  useSwitchChain,
  useWalletClient
} from 'wagmi';

import { buildConnectTypedData } from '@/lib/auth/buildConnectTypedData';
import { mapConnectorToProvider } from '@/lib/web3/walletProvider.map';
import { getNonceAction } from '@/server/actions/auth/getNonce.action';
import { signInAction } from '@/server/actions/auth/signIn.action';
import { getUserAction } from '@/server/actions/user/getUser.action';
import {
  C_ACTIVE_NETWORK_ID,
  C_SUPPORTED_NETWORK_IDS
} from '@/ts/constants/C_NETWORKS';

import type {
  T_AuthConnectReq,
  T_WalletProvider
} from '@/ts/types/T_AuthConnectReq';
import type { T_UserApiResponse } from '@/ts/types/T_UserApiResponse';

/**
 * Orchestrator: connect → ensure chain → get nonce → sign → signIn → getUser
 * Mutex prevents duplicate runs (StrictMode, popups, rapid clicks).
 */
type T_Opts = {
  walletProvider?: T_WalletProvider;
  onConnected?: () => void;
};

export function useWalletAuth(
  updateGlobalData?: (v: { user: T_UserApiResponse['data'] }) => void,
  t?: (key: string) => string
) {
  const { address, connector, chainId, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { data: walletClient } = useWalletClient();
  const { switchChainAsync } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();
  const router = useRouter();

  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to check if we're on the reward subdomain
  const isRewardSubdomain = (): boolean => {
    if (typeof window === 'undefined') return false;
    const normalizedSubdomain = (
      process.env.NEXT_PUBLIC_SUBDOMAIN_NAME || 'reward'
    ).toLowerCase();
    const hostname = window.location.hostname.toLowerCase();
    return (
      normalizedSubdomain.length > 0 &&
      hostname.startsWith(`${normalizedSubdomain}.`)
    );
  };

  // Helper: Sleep
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Helper: Wait for Page Visibility (Mobile Deep Link Fix)
  const waitForVisible = async () => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') {
      console.log('Page is visible, proceeding...');
      return;
    }
    console.log('Page hidden, waiting for user to return...');
    await new Promise<void>(resolve => {
      const handler = () => {
        if (document.visibilityState === 'visible') {
          console.log('User returned to page!');
          document.removeEventListener('visibilitychange', handler);
          resolve();
        }
      };
      document.addEventListener('visibilitychange', handler);
    });
    // Small buffer after becoming visible
    await sleep(500);
  };

  const run = useCallback(
    async (opts?: T_Opts) => {
      // Mutex: prevent parallel execution
      if (inFlightRef.current) {
        console.log('Skipping auth run: already in progress');
        return inFlightRef.current;
      }

      console.log('--- STARTING WALLET AUTH RUN ---');
      const job = (async () => {
        try {
          // 0) Ensure connected wallet
          let usedAddress = address;
          let usedConnector = connector;

          if (!isConnected) {
            console.log('Wallet not connected, initiating connection...');
            const prefer =
              connectors.find(c => c.id === 'walletConnect') ??
              connectors.find(c => c.id === 'injected') ??
              connectors[0];

            if (!prefer) throw new Error('No wallet connector available');

            // Optimistic connect: try passing chainId to avoid switch prompt
            // This reduces the number of deep-link hops on mobile
            try {
              console.log(
                'Attempting optimistic connect with chainId:',
                C_ACTIVE_NETWORK_ID
              );
              const res = await connectAsync({
                connector: prefer,
                chainId: C_ACTIVE_NETWORK_ID
              });
              usedAddress = res.accounts[0];
              console.log('Optimistic connect successful');
            } catch (optError) {
              console.warn(
                'Optimistic connect failed, falling back to standard connect:',
                optError
              );
              // Fallback without chainId
              const res = await connectAsync({ connector: prefer });
              usedAddress = res.accounts[0];
            }
            usedConnector = prefer;
            console.log('Connection established:', usedAddress);

            // Settle delay after connection (Increased for mobile reliability)
            // Mobile wallets need time to restore context after the app switch
            console.log('Waiting for wallet to settle (1500ms)...');
            await sleep(1500);
          } else {
            console.log('Already connected:', usedAddress);
          }

          if (!usedAddress) throw new Error('No wallet address after connect');

          // Ensure visible before proceeding to network checks
          await waitForVisible();

          // 1) Ensure wallet runs on the configured network
          // We check fresh chainId from wagmi, or fallback to client
          let activeChainId =
            chainId ?? walletClient?.chain?.id ?? C_ACTIVE_NETWORK_ID;

          try {
            if (
              activeChainId &&
              !C_SUPPORTED_NETWORK_IDS.includes(activeChainId as number)
            ) {
              console.log('Switching network...');
              // Switch to the configured network if current network is not supported
              const sw = await switchChainAsync({
                chainId: C_ACTIVE_NETWORK_ID
              });
              activeChainId = sw.id;
              console.log('Network switched to:', activeChainId);
              // Settle delay after switch (MetaMask Mobile needs this)
              await sleep(1500);
            }
          } catch (e) {
            console.warn('Network switch failed or cancelled', e);
            toast.info(t?.('networkSwitchCancelled'));
            throw e;
          }

          // Ensure visible before requesting Nonce (backend call)
          await waitForVisible();

          // 2) Nonce
          console.log('Fetching nonce...');
          const nonceResp = await getNonceAction();
          const nonce = (nonceResp as { data?: { nonce?: string } })?.data
            ?.nonce;
          if (typeof nonce !== 'string' || !nonce) {
            throw new Error('Nonce is missing from backend');
          }
          console.log('Nonce received:', nonce);

          // 3) Typed data preparation
          const providerName =
            opts?.walletProvider ??
            usedConnector?.name ??
            connectors.find(c => c.id === 'walletConnect')?.name ??
            connectors.find(c => c.id === 'injected')?.name ??
            connectors[0]?.name;
          const provider = mapConnectorToProvider(providerName);

          const typed = buildConnectTypedData({
            address: usedAddress,
            nonce,
            chainId: activeChainId,
            walletProvider: provider
          });

          // Ensure visible before Signature Request (CRITICAL)
          await waitForVisible();

          // 4) Sign with Timeout
          let signature: `0x${string}`;

          try {
            console.log('Requesting signature...');

            // Define signature promise - Prefer signTypedDataAsync as it handles the active connector internally
            // This avoids the stale walletClient closure issue
            const signPromise = async () => {
              return signTypedDataAsync({
                account: usedAddress as `0x${string}`,
                domain: typed.domain,
                types: typed.types,
                primaryType: typed.primaryType,
                message: typed.message
              });
            };

            // Wrap in timeout
            const TIMEOUT_MS = 60000; // 60s hard timeout
            const timeoutPromise = new Promise<never>((_, reject) =>
              setTimeout(() => {
                const err = new Error('Signature request timed out');
                (err as any).code = 'SIGN_TIMEOUT';
                reject(err);
              }, TIMEOUT_MS)
            );

            signature = await Promise.race([signPromise(), timeoutPromise]);
            console.log('Signature received!');
          } catch (e: unknown) {
            console.error('Signature failed:', e);
            const err = e as { message?: string; code?: number | string };

            if (
              err?.message?.includes('Provided chainId') ||
              err?.message?.includes('Chain ID')
            ) {
              toast.info(t?.('switchToSupportedNetwork'));
            } else if (
              err?.code === 4001 ||
              (typeof err?.message === 'string' &&
                err.message.includes('User rejected'))
            ) {
              toast.info(t?.('signatureRejected'));
            } else if (err?.code === 'SIGN_TIMEOUT') {
              toast.error(
                t?.('signatureTimedOut') ||
                  'Signature timed out. Please try again.'
              );
            }
            throw e;
          }

          // 5) Sign-in (cookies set by actionRequest)
          console.log('Signing in...');
          const body: T_AuthConnectReq = {
            address: usedAddress as `0x${string}`,
            nonce,
            signature,
            chainId: activeChainId,
            walletProvider: provider
          };
          const signInRes = await signInAction(body);
          console.log('Sign-in result:', signInRes);

          // 6) Gate by status: single toast on failure, otherwise fetch user
          const okToFetchUser = (s: number) =>
            s === 200 || s === 201 || s === 204 || s === 409;

          const status = signInRes?.status ?? 0;
          if (!okToFetchUser(status)) {
            toast.error(t?.('signInFailed'));
            const err = new Error('Login failed') as Error & {
              status?: number;
              cause?: unknown;
            };
            err.status = status;
            err.cause = signInRes?.data;
            throw err;
          }

          try {
            console.log('Fetching user data...');
            const resp = await getUserAction({ setResponseCookies: false });
            // robust extraction (covers {data:{user}}, {data:{data:{user}}}, {user})
            const nextUser = (resp as T_UserApiResponse)?.data ?? null;

            // IMPORTANT: only write when we have a value; don't clobber with null
            if (nextUser) {
              updateGlobalData?.({ user: nextUser });
              if (signInRes?.data?.isNew) {
                opts?.onConnected?.();
              }

              // Refresh router to get all server-side data properly (only on reward subdomain)
              if (isRewardSubdomain()) {
                router.refresh();
              }
              console.log('--- AUTH RUN COMPLETE ---');
            } else {
              console.warn('getUserAction returned no user. Raw:', resp);
            }
          } catch (e) {
            console.error('getUser after sign-in failed', e);
          }
        } finally {
          if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
          releaseTimerRef.current = setTimeout(() => {
            inFlightRef.current = null;
            releaseTimerRef.current = null;
            console.log('Auth lock released');
          }, 300);
        }
      })();

      inFlightRef.current = job;
      return job;
    },
    [
      address,
      chainId,
      connector,
      connectors,
      connectAsync,
      isConnected,
      signTypedDataAsync,
      switchChainAsync,
      updateGlobalData,
      walletClient,
      router,
      t
    ]
  );

  useEffect(() => {
    return () => {
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
      inFlightRef.current = null;
    };
  }, []);

  return { run };
}
